import { MouseEvent, forwardRef } from "react";
import { Button, ButtonProps } from "../Button";
import { useDrillDownContext } from "./internal/useDrillDownContext";
import { DataTrackingId } from "../../types";
import { useTrackingId } from "../../hooks";

/**
 * Props for the DrillDownNextButton component.
 *
 * @extends ButtonProps
 * @extends DataTrackingId
 */
export type DrillDownNextButtonProps = ButtonProps & DataTrackingId;

/**
 * Button that navigates to the next drilldown screen.
 *
 * Increments the drilldown index by one when clicked. Automatically integrates with the
 * DrillDown context for navigation. Preserves custom onClick handlers.
 *
 * Features:
 * - Automatic navigation to next index
 * - Automatic analytics tracking via data-tracking-id
 * - All Button component props supported
 * - Default accessible label for screen readers
 *
 * @example
 * ```tsx
 * <DrillDown.NextButton appearance="primary">Continue</DrillDown.NextButton>
 * <DrillDown.NextButton onClick={saveData}>Save and Continue</DrillDown.NextButton>
 * ```
 */
export const DrillDownNextButton = forwardRef<
  HTMLButtonElement,
  DrillDownNextButtonProps
>((props, ref) => {
  const { onClick, ...rest } = props;
  const { next } = useDrillDownContext();

  const data = {
    title: props.title,
    appearance: props.appearance,
    size: props.size,
    type: props.type,
  };

  const trackingId = useTrackingId({
    name: "DrillDownNextButton",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    next?.();
  };

  return (
    <Button
      aria-label="Next"
      data-anv="drilldown-next-button"
      data-tracking-id={trackingId}
      onClick={onClickHandler}
      ref={ref}
      {...rest}
    />
  );
});

DrillDownNextButton.displayName = "DrillDownNextButton";
